// The contributor's qualification test: which languages can be tested, which
// one is selected, past submissions, and the test-taking session itself.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { languageApi } from "@/api/language.api";
import { testsApi, type ContributorTestEligibility } from "@/api/tests.api";
import { canAccessContributorWorkspace } from "@/lib/auth/roles";
import { normalizeLanguageKey } from "../helpers";
import { useTestTaking } from "./useTestTaking";
import { buildSubmissionMap, eligibilityByLanguage, topEligibilityLanguage, type SubmissionInfo } from "../logic/earnings-logic";
import type { GetPaidBase } from "./get-paid-context";
import type { Language } from "@/lib/types/language";

interface QualificationTestInput extends GetPaidBase {
  currentLang: string;
  testEligibility: ContributorTestEligibility | null;
  /** A validation contract still to sign or to final-validate blocks a new test. */
  hasValidationContractInFlight: boolean;
}

export function useQualificationTest({ t, user, toast, currentLang, testEligibility, hasValidationContractInFlight }: QualificationTestInput) {
  const [mySubmissions, setMySubmissions] = useState<Record<string, SubmissionInfo>>({});
  // Kept as before the split: the toast's type is wider than useTestTaking's.
  const testTaking = useTestTaking(t, toast, currentLang, mySubmissions, setMySubmissions);
  const { selectedTestLang, setSelectedTestLang, startingTest } = testTaking;

  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const userChangedTestLangRef = useRef(false);

  const testEligibilityByLanguage = useMemo(() => eligibilityByLanguage(testEligibility), [testEligibility]);
  const contributedTestLanguages = useMemo(
    () =>
      availableLanguages.filter((lang) => {
        const code = normalizeLanguageKey(lang.code);
        return Boolean(code && testEligibilityByLanguage[code]?.totalAudioContributions > 0);
      }),
    [availableLanguages, testEligibilityByLanguage],
  );
  const selectedLanguageEligibility = testEligibilityByLanguage[normalizeLanguageKey(selectedTestLang)] ?? null;

  const selectTestLanguage = (languageCode: string) => {
    userChangedTestLangRef.current = true;
    setSelectedTestLang(languageCode);
  };

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        setLoadingLanguages(true);
        const languages = await languageApi.getLanguages();
        setAvailableLanguages(languages.filter((lang) => lang.active));
      } catch {
        // No language list: the selector stays empty.
      } finally {
        setLoadingLanguages(false);
      }
    };
    loadLanguages();
  }, []);

  // Until the user picks one, prefer a language they contributed in.
  useEffect(() => {
    if (userChangedTestLangRef.current || !contributedTestLanguages.length) {
      return;
    }
    const contributedCodes = new Set(contributedTestLanguages.map((lang) => normalizeLanguageKey(lang.code)));
    if (contributedCodes.has(normalizeLanguageKey(selectedTestLang))) return;
    const preferredLanguage = user?.contributionLanguages?.find((lang) => contributedCodes.has(normalizeLanguageKey(lang)))
      ?? contributedTestLanguages[0]?.code;
    if (preferredLanguage) {
      setSelectedTestLang(preferredLanguage);
    }
  }, [contributedTestLanguages, selectedTestLang, setSelectedTestLang, user?.contributionLanguages]);

  const loadMySubmissionStatus = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await testsApi.getMySubmissions();
      const items = Array.isArray(res?.items) ? res.items : [];
      setMySubmissions(buildSubmissionMap(items));
    } catch {
      // Without it the test can still be started; the server refuses duplicates.
    }
  }, [user?.id]);

  useEffect(() => {
    if (canAccessContributorWorkspace(user?.role)) {
      loadMySubmissionStatus();
    }
  }, [user?.id, user?.role, loadMySubmissionStatus]);

  // Then the language closest to the test threshold wins.
  useEffect(() => {
    if (userChangedTestLangRef.current) return;
    const topLang = topEligibilityLanguage(testEligibilityByLanguage);
    if (topLang && topLang !== normalizeLanguageKey(selectedTestLang)) {
      setSelectedTestLang(topLang);
    }
  }, [selectedTestLang, setSelectedTestLang, testEligibilityByLanguage]);

  const selectedSubmission = mySubmissions[normalizeLanguageKey(selectedTestLang)];
  const isStartTestBlocked =
    startingTest ||
    Boolean(selectedSubmission) ||
    hasValidationContractInFlight ||
    !selectedLanguageEligibility?.eligible;

  return {
    testTaking, mySubmissions,
    availableLanguages, loadingLanguages, contributedTestLanguages,
    testEligibilityByLanguage, selectedLanguageEligibility, selectTestLanguage,
    selectedSubmission, isStartTestBlocked,
  };
}
